import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Flex, Text, Button, Badge, HStack, VStack, Divider, IconButton,
  Input, Textarea, Select, Checkbox, FormControl, FormLabel, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, useToast, Spinner, Alert, AlertIcon, useDisclosure,
} from '@chakra-ui/react';
import {
  FiAlertCircle, FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiChevronDown,
  FiChevronUp, FiAlertTriangle, FiInfo, FiUser, FiCalendar, FiArrowLeft,
} from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_COLORS = {
  open: 'red', discovery: 'orange', action: 'yellow',
  reporting: 'blue', closed: 'green', closed_reported: 'teal',
};
const STATUS_LABELS = {
  open: 'Open', discovery: 'Discovery', action: 'Action',
  reporting: 'Reporting', closed: 'Closed', closed_reported: 'Closed & Reported',
};
const ROW_TYPES = [
  { value: 'observation',   label: 'Observation',   color: 'blue',   desc: 'General information or note' },
  { value: 'investigation', label: 'Investigation', color: 'purple', desc: 'Active investigation' },
  { value: 'plan',          label: 'Plan',          color: 'cyan',   desc: 'Planned response action' },
  { value: 'action',        label: 'Action',        color: 'orange', desc: 'Concrete action taken' },
  { value: 'mitigation',    label: 'Mitigation',    color: 'teal',   desc: 'Mitigation measures applied' },
  { value: 'loss_or_damage',label: 'Loss or Damage',color: 'red',    desc: 'Known data breach or broken items' },
  { value: 'risk',          label: 'Risk',          color: 'pink',   desc: 'Identified risk or threat' },
  { value: 'reporting',     label: 'Reporting',     color: 'teal',   desc: 'Official reporting activity' },
  { value: 'correction_log',label: 'Correction Log',color: 'gray',   desc: 'Correction to a previous entry' },
];

const fmtDt = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const toLocalDT = (d) => d ? new Date(new Date(d).getTime() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,16) : '';

// ── Incident Row Component ────────────────────────────────────────────────────
function IncidentRow({ row, incident, allUsers, allRows, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const type = ROW_TYPES.find(t => t.value === row.row_type) || ROW_TYPES[0];
  const correctsRow = row.corrects_row_id ? allRows.find(r => r.id === row.corrects_row_id) : null;

  return (
    <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden"
      borderLeft="4px solid" sx={{ borderLeftColor: `${type.color}.400` }}>
      <Flex px={3} py={2} align="center" gap={3} bg="white" cursor="pointer" onClick={() => setExpanded(!expanded)}
        _hover={{ bg: 'gray.50' }}>
        <Badge colorScheme={type.color} fontSize="9px" flexShrink={0}>{type.label}</Badge>
        {row.edited && <Badge colorScheme="gray" fontSize="8px" variant="outline">Edited</Badge>}
        <Text fontSize="12px" color="gray.500" flexShrink={0}>{fmtDt(row.row_timestamp)}</Text>
        <Text fontSize="12px" fontWeight="600" color="gray.700" flexShrink={0}>{row.user_name || 'Unknown'}</Text>
        <Text fontSize="12px" color="gray.600" noOfLines={1} flex={1}>{row.notes || '—'}</Text>
        <HStack spacing={1} flexShrink={0}>
          <Tooltip label="Edit (will mark as edited)">
            <IconButton icon={<FiEdit2 size={11} />} size="xs" variant="ghost" colorScheme="gray"
              onClick={e => { e.stopPropagation(); onEdit(row); }} />
          </Tooltip>
          <IconButton icon={<FiTrash2 size={11} />} size="xs" variant="ghost" colorScheme="red"
            onClick={e => { e.stopPropagation(); onDelete(row.id); }} />
          {expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
        </HStack>
      </Flex>

      {expanded && (
        <Box px={4} py={3} bg="gray.50" borderTop="1px" borderColor="gray.100">
          {correctsRow && (
            <Alert status="warning" size="sm" mb={3} borderRadius="md" py={1}>
              <AlertIcon boxSize={3} />
              <Text fontSize="11px">Corrects entry from {fmtDt(correctsRow.row_timestamp)}: {correctsRow.notes?.slice(0,80)}</Text>
            </Alert>
          )}
          {row.row_type === 'reporting' && (
            <Alert status="info" size="sm" mb={3} borderRadius="md" py={1}>
              <AlertIcon boxSize={3} />
              <Text fontSize="11px">Official reporting entry. Record whether report has been made, to whom, reference numbers and content summary in notes.</Text>
            </Alert>
          )}
          {row.notes && (
            <Box mb={2}>
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>Notes</Text>
              <Text fontSize="13px" color="gray.700" whiteSpace="pre-wrap">{row.notes}</Text>
            </Box>
          )}
          {row.further_action && (
            <Box mb={2}>
              <Text fontSize="10px" fontWeight="700" color="orange.400" textTransform="uppercase" mb={1}>Further Action / Next Steps</Text>
              <Text fontSize="13px" color="gray.700" whiteSpace="pre-wrap">{row.further_action}</Text>
              {row.task_created_id && <Badge colorScheme="green" fontSize="8px" mt={1}>✓ Task created</Badge>}
            </Box>
          )}
          {(row.companies?.length > 0 || row.contacts?.length > 0 || row.affected_users?.length > 0 || row.resources?.length > 0) && (
            <VStack spacing={0.5} align="stretch" mt={2}>
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={0.5}>Affected / Involved</Text>
              {row.companies?.map(c => (
                <HStack key={c.company_id} spacing={1}>
                  <Badge colorScheme="purple" fontSize="7px">Co</Badge>
                  <Text fontSize="11px">{c.name}</Text>
                  {c.description && <Text fontSize="10px" color="gray.400">— {c.description}</Text>}
                </HStack>
              ))}
              {row.contacts?.map(c => (
                <HStack key={c.contact_id} spacing={1}>
                  <Badge colorScheme="blue" fontSize="7px">Ct</Badge>
                  <Text fontSize="11px">{c.first_name} {c.last_name}</Text>
                  {c.description && <Text fontSize="10px" color="gray.400">— {c.description}</Text>}
                </HStack>
              ))}
              {row.affected_users?.map(u => (
                <HStack key={u.user_id} spacing={1}>
                  <Badge colorScheme="teal" fontSize="7px">User</Badge>
                  <Text fontSize="11px">{u.display_name}</Text>
                  {u.description && <Text fontSize="10px" color="gray.400">— {u.description}</Text>}
                </HStack>
              ))}
              {row.resources?.map(r => (
                <HStack key={r.resource_id} spacing={1}>
                  <Badge colorScheme="orange" fontSize="7px">Res</Badge>
                  <Text fontSize="11px">{r.name}</Text>
                  {r.description && <Text fontSize="10px" color="gray.400">— {r.description}</Text>}
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Add/Edit Row Form ─────────────────────────────────────────────────────────
function RowForm({ incidentId, allUsers, allRows, editRow, onSaved, onCancel, currentUser }) {
  const toast = useToast();
  const defaultUserId = currentUser?.dbId || currentUser?.id || '';
  const [form, setForm] = useState({
    row_type: editRow?.row_type || 'observation',
    row_timestamp: toLocalDT(editRow?.row_timestamp || new Date()),
    user_id: editRow?.user_id || defaultUserId,
    notes: editRow?.notes || '',
    further_action: editRow?.further_action || '',
    create_task: false,
    corrects_row_id: editRow?.corrects_row_id || '',
    companies: [],
    contacts: [],
    affected_users: [],
    resources: [],
  });
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmEdit, setConfirmEdit] = useState(false);

  const searchCompanies = async (q) => {
    if (q.length < 2) { setCompanyResults([]); return; }
    const { data } = await api.get(`/companies?search=${encodeURIComponent(q)}&per_page=8`);
    setCompanyResults(data.data || data || []);
  };
  const searchContacts = async (q) => {
    if (q.length < 2) { setContactResults([]); return; }
    const { data } = await api.get(`/contacts?search=${encodeURIComponent(q)}&per_page=8`);
    setContactResults(data.data || data || []);
  };

  const handleSave = async () => {
    if (editRow && !confirmEdit) { setConfirmEdit(true); return; }
    setSaving(true);
    try {
      let result;
      if (editRow) {
        const { data } = await api.put(`/incidents/${incidentId}/rows/${editRow.id}`, form);
        result = data;
      } else {
        const { data } = await api.post(`/incidents/${incidentId}/rows`, form);
        result = data;
      }
      onSaved(result);
    } catch(e) { toast({ title: 'Failed to save', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const correctionRows = allRows.filter(r => r.id !== editRow?.id && r.row_type !== 'correction_log');

  return (
    <Box border="1px" borderColor={editRow?'orange.200':'purple.200'} borderRadius="md" p={4}
      bg={editRow?'orange.50':'purple.50'}>
      {confirmEdit && (
        <Alert status="warning" mb={3} borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontSize="13px" fontWeight="700">Are you sure you wish to edit this entry?</Text>
            <Text fontSize="11px" color="gray.600">We recommend creating a Correction Log entry below instead. Editing changes the original record.</Text>
            <HStack mt={2}>
              <Button size="xs" colorScheme="orange" onClick={() => { setConfirmEdit(false); setSaving(true); api.put(`/incidents/${incidentId}/rows/${editRow.id}`, form).then(({ data }) => { onSaved(data); }).catch(() => toast({ title:'Failed', status:'error', duration:2000 })).finally(() => setSaving(false)); }}>
                Yes, Edit Anyway
              </Button>
              <Button size="xs" variant="ghost" onClick={() => setConfirmEdit(false)}>Cancel</Button>
            </HStack>
          </Box>
        </Alert>
      )}

      <VStack spacing={3} align="stretch">
        <HStack spacing={3} flexWrap="wrap">
          <FormControl w="180px">
            <FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
            <Select size="sm" value={form.row_type} onChange={e => set('row_type', e.target.value)}>
              {ROW_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>)}
            </Select>
          </FormControl>
          <FormControl flex={1}>
            <FormLabel fontSize="xs" mb={0.5}>Date & Time</FormLabel>
            <Input size="sm" type="datetime-local" value={form.row_timestamp}
              onChange={e => set('row_timestamp', e.target.value)} />
          </FormControl>
          <FormControl w="200px">
            <FormLabel fontSize="xs" mb={0.5}>Logged By</FormLabel>
            <Select size="sm" value={form.user_id} onChange={e => set('user_id', e.target.value)}>
              <option value="">Select user…</option>
              {allUsers.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
            </Select>
          </FormControl>
        </HStack>

        {form.row_type === 'correction_log' && correctionRows.length > 0 && (
          <FormControl>
            <FormLabel fontSize="xs" mb={0.5}>Corrects Entry</FormLabel>
            <Select size="sm" value={form.corrects_row_id} onChange={e => set('corrects_row_id', e.target.value)}>
              <option value="">Select entry to correct…</option>
              {correctionRows.map(r => (
                <option key={r.id} value={r.id}>{fmtDt(r.row_timestamp)} — {r.row_type} — {r.notes?.slice(0,60)}</option>
              ))}
            </Select>
          </FormControl>
        )}

        {form.row_type === 'reporting' && (
          <Alert status="info" size="sm" borderRadius="md" py={2}>
            <AlertIcon boxSize={3} />
            <Text fontSize="11px">Note: Record whether the report has been made, who it was sent to, reference numbers, and a summary of contents in the notes below.</Text>
          </Alert>
        )}

        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Notes / Observations</FormLabel>
          <Textarea size="sm" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder="Describe what was observed, discovered, or actioned…"
            style={{ resize: 'vertical', minHeight: '72px' }} />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Further Action / Next Steps / Investigation Parameters</FormLabel>
          <Textarea size="sm" rows={2} value={form.further_action} onChange={e => set('further_action', e.target.value)}
            placeholder="What happens next? Investigation parameters, assigned actions…"
            style={{ resize: 'vertical', minHeight: '56px' }} />
        </FormControl>

        {form.further_action && !editRow && (
          <Checkbox size="sm" isChecked={form.create_task}
            onChange={e => set('create_task', e.target.checked)} colorScheme="orange">
            <Text fontSize="12px">Create incident task in project (with this action as a sub-task)</Text>
          </Checkbox>
        )}

        {/* Companies affected */}
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Companies Affected</FormLabel>
          <Flex gap={1} flexWrap="wrap" mb={1}>
            {form.companies.map((c, i) => (
              <Badge key={i} colorScheme="purple" fontSize="10px" display="flex" alignItems="center" gap={1}>
                {c.name}
                <Box as="span" cursor="pointer" ml={1} onClick={() => set('companies', form.companies.filter((_, j) => j !== i))}>×</Box>
              </Badge>
            ))}
          </Flex>
          <Input size="xs" placeholder="Search company…" value={companySearch}
            onChange={e => { setCompanySearch(e.target.value); searchCompanies(e.target.value); }} />
          {companyResults.length > 0 && (
            <Box border="1px" borderColor="gray.200" borderRadius="md" mt={1} maxH="100px" overflowY="auto" bg="white">
              {companyResults.map(c => (
                <Box key={c.id} px={2} py={1} fontSize="11px" cursor="pointer" _hover={{ bg:'purple.50' }}
                  onClick={() => { if (!form.companies.find(x => x.id === c.id)) set('companies', [...form.companies, c]); setCompanySearch(''); setCompanyResults([]); }}>
                  {c.name}
                </Box>
              ))}
            </Box>
          )}
        </FormControl>

        {/* Contacts affected */}
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Contacts Affected / Involved</FormLabel>
          <Flex gap={1} flexWrap="wrap" mb={1}>
            {form.contacts.map((c, i) => (
              <HStack key={i} spacing={1} bg="blue.50" px={2} py={0.5} borderRadius="md" border="1px" borderColor="blue.200">
                <Badge colorScheme="blue" fontSize="7px">Ct</Badge>
                <Text fontSize="11px">{c.first_name} {c.last_name}</Text>
                {c.description && <Text fontSize="10px" color="gray.500">— {c.description}</Text>}
                <Box as="span" cursor="pointer" onClick={() => set('contacts', form.contacts.filter((_, j) => j !== i))} color="red.400" fontSize="10px">✕</Box>
              </HStack>
            ))}
          </Flex>
          <Input size="xs" placeholder="Search contact…" value={contactSearch}
            onChange={e => { setContactSearch(e.target.value); searchContacts(e.target.value); }} />
          {contactResults.length > 0 && (
            <Box border="1px" borderColor="gray.200" borderRadius="md" mt={1} maxH="100px" overflowY="auto" bg="white">
              {contactResults.map(c => (
                <Box key={c.id} px={2} py={1} fontSize="11px" cursor="pointer" _hover={{ bg:'blue.50' }}
                  onClick={() => {
                    if (!form.contacts.find(x => x.id === c.id))
                      set('contacts', [...form.contacts, { id: c.id, first_name: c.first_name, last_name: c.last_name, description: '' }]);
                    setContactSearch(''); setContactResults([]);
                  }}>
                  <Flex justify="space-between">
                    <Text>{c.first_name} {c.last_name}</Text>
                    <Text fontSize="9px" color="gray.400">{c.email}</Text>
                  </Flex>
                </Box>
              ))}
            </Box>
          )}
        </FormControl>

        {/* Users affected */}
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Users Affected / Involved</FormLabel>
          <Flex gap={1} flexWrap="wrap" mb={1}>
            {(form.affected_users||[]).map((u, i) => (
              <HStack key={i} spacing={1} bg="teal.50" px={2} py={0.5} borderRadius="md" border="1px" borderColor="teal.200">
                <Badge colorScheme="teal" fontSize="7px">User</Badge>
                <Text fontSize="11px">{u.display_name || u.username}</Text>
                {u.description && <Text fontSize="10px" color="gray.500">— {u.description}</Text>}
                <Box as="span" cursor="pointer" onClick={() => set('affected_users', (form.affected_users||[]).filter((_, j) => j !== i))} color="red.400" fontSize="10px">✕</Box>
              </HStack>
            ))}
          </Flex>
          <Select size="xs" placeholder="Add user…" value=""
            onChange={e => {
              const u = allUsers.find(x => x.id === e.target.value);
              if (u && !(form.affected_users||[]).find(x => x.id === u.id))
                set('affected_users', [...(form.affected_users||[]), { id: u.id, display_name: u.display_name||u.username, description: '' }]);
            }}>
            {allUsers.filter(u => !(form.affected_users||[]).find(x => x.id === u.id)).map(u => (
              <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
            ))}
          </Select>
        </FormControl>

        {/* Resources affected */}
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Resources Affected / Involved</FormLabel>
          <Flex gap={1} flexWrap="wrap" mb={1}>
            {(form.resources||[]).map((r, i) => (
              <HStack key={i} spacing={1} bg="orange.50" px={2} py={0.5} borderRadius="md" border="1px" borderColor="orange.200">
                <Badge colorScheme="orange" fontSize="7px">Res</Badge>
                <Text fontSize="11px">{r.name}</Text>
                {r.description && <Text fontSize="10px" color="gray.500">— {r.description}</Text>}
                <Box as="span" cursor="pointer" onClick={() => set('resources', (form.resources||[]).filter((_, j) => j !== i))} color="red.400" fontSize="10px">✕</Box>
              </HStack>
            ))}
          </Flex>
          <Select size="xs" placeholder="Add resource…" value=""
            onChange={async e => {
              if (!e.target.value) return;
              const { data } = await api.get('/resources/' + e.target.value).catch(() => ({ data: null }));
              if (data && !(form.resources||[]).find(x => x.id === data.id))
                set('resources', [...(form.resources||[]), { id: data.id, name: data.name, description: '' }]);
            }}>
            <option value="">Select resource…</option>
          </Select>
          <Text fontSize="9px" color="gray.400" mt={0.5}>Search resources via /resources endpoint or type resource name</Text>
        </FormControl>

        <HStack justify="flex-end">
          <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button size="sm" bg={editRow?'orange.500':'#1A365D'} color="white"
            _hover={{ bg: editRow?'orange.600':'#2B6CB0' }}
            leftIcon={<FiCheck size={13} />} isLoading={saving} onClick={handleSave}>
            {editRow ? 'Save Edit' : 'Add Entry'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}

// ── Main IncidentBlogPage ─────────────────────────────────────────────────────
export default function IncidentBlogPage({ taskId, taskTitle, projectId, isOpen, onClose, onRefresh, initialIncidentId }) {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const toast = useToast();

  const defaultNoticed = '';
  const [incForm, setIncForm] = useState({
    title: '', description: '', started_at: toLocalDT(new Date()), finished_at: '', is_open: true,
    noticed_by: defaultNoticed,
    data_leaked: false, data_lost: false, system_unavailable: false,
    backup_status: 'not_applicable', pr_issue: false, non_disruptive: false,
  });

  const fetchIncidents = useCallback(async () => {
    if (!taskId || !isOpen) return;
    setLoading(true);
    try {
      const [incRes, usersRes] = await Promise.all([
        api.get(`/incidents?task_id=${taskId}`),
        api.get('/users'),
      ]);
      setIncidents(incRes.data || []);
      setAllUsers(usersRes.data || []);
    } catch(e) {}
    setLoading(false);
  }, [taskId, isOpen]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  // Auto-open a specific incident if initialIncidentId is provided
  useEffect(() => {
    if (initialIncidentId && isOpen) {
      api.get(`/incidents/${initialIncidentId}`)
        .then(({ data }) => setSelected(data))
        .catch(() => {});
    }
  }, [initialIncidentId, isOpen]);

  // Default noticed_by to logged-in user once users are loaded
  useEffect(() => {
    if (allUsers.length) {
      const meId = user?.id; // UUID (user.dbId does not exist on frontend; _dbId is internal)
      const me = allUsers.find(u => u.id === meId);
      if (me) setIncForm(p => ({ ...p, noticed_by: me.id }));
    }
  }, [allUsers, user]);

  const fetchSelected = async (id) => {
    const { data } = await api.get(`/incidents/${id}`);
    setSelected(data);
  };

  const handleCreateIncident = async () => {
    try {
      const { data } = await api.post('/incidents', { ...incForm, task_id: taskId, project_id: projectId });
      setIncidents(prev => [data, ...prev]);
      setSelected(data);
      setCreating(false);
      if (onRefresh) onRefresh();
      toast({ title: 'Incident created', status: 'success', duration: 2000 });
    } catch(e) { toast({ title: 'Failed to create', status: 'error', duration: 2000 }); }
  };

  const handleDeleteIncident = async (id) => {
    await api.delete(`/incidents/${id}`);
    setIncidents(prev => prev.filter(i => i.id !== id));
    if (selected?.id === id) setSelected(null);
    setDeleteConfirmId(null);
    if (onRefresh) onRefresh();
  };

  const handleRowSaved = (updatedIncident) => {
    setSelected(updatedIncident);
    setAddingRow(false);
    setEditingRow(null);
    setIncidents(prev => prev.map(i => i.id === updatedIncident.id ? { ...i, status: updatedIncident.status } : i));
    if (onRefresh) onRefresh();
  };

  const handleDeleteRow = async (rowId) => {
    const { data } = await api.delete(`/incidents/${selected.id}/rows/${rowId}`);
    setSelected(data);
    setIncidents(prev => prev.map(i => i.id === data.id ? { ...i, status: data.status } : i));
  };

  const handleToggleOpen = async (inc) => {
    const { data } = await api.put(`/incidents/${inc.uuid || inc.id}`, { is_open: !inc.is_open });
    setSelected(data);
    setIncidents(prev => prev.map(i => i.id === data.id ? data : i));
    if (onRefresh) onRefresh();
  };

  const iset = (k, v) => setIncForm(p => ({ ...p, [k]: v }));

  if (!isOpen) return null;

  // Full-page overlay
  return (
    <Box position="fixed" inset={0} zIndex={1400} bg="rgba(0,0,0,0.5)" overflowY="auto">
      <Box maxW="900px" mx="auto" my="32px" px={4} pb={16}>
        {/* Header */}
        <Flex align="center" justify="space-between" mb={6}
          bg="white" borderRadius="xl" px={5} py={4} shadow="lg">
          <HStack spacing={3}>
            <Box bg="red.500" borderRadius="full" w="36px" h="36px" display="flex" alignItems="center" justifyContent="center">
              <FiAlertCircle size={20} color="white" />
            </Box>
            <Box>
              <Text fontSize="20px" fontWeight="800" color="gray.800">Incident Log</Text>
              {taskTitle && <Text fontSize="12px" color="gray.500">{taskTitle}</Text>}
            </Box>
          </HStack>
          <IconButton icon={<FiX size={20} />} variant="ghost" colorScheme="gray" onClick={onClose}
            aria-label="Close" />
        </Flex>

        {/* Incident list or detail */}
        {!selected && !creating ? (
          <Box bg="white" borderRadius="xl" shadow="2xl" overflow="hidden">
            <Flex px={5} py={4} bg="gray.50" borderBottom="1px" borderColor="gray.200" align="center" justify="space-between">
              <HStack>
                <Text fontWeight="700" fontSize="15px">Incidents</Text>
                <Badge colorScheme="red" fontSize="9px">{incidents.filter(i=>i.is_open).length} open</Badge>
                <Badge colorScheme="gray" fontSize="9px">{incidents.length} total</Badge>
              </HStack>
              <Button size="sm" leftIcon={<FiPlus size={13} />} colorScheme="red"
                onClick={() => { setIncForm({ title:'', description:'', started_at: toLocalDT(new Date()), finished_at:'', is_open:true, noticed_by:'', data_leaked:false, data_lost:false, system_unavailable:false, backup_status:'not_applicable', pr_issue:false, non_disruptive:false }); setCreating(true); }}>
                New Incident
              </Button>
            </Flex>

            {loading && <Box py={12} textAlign="center"><Spinner color="red.500" /></Box>}

            {!loading && incidents.length === 0 && (
              <Box textAlign="center" py={16}>
                <Box fontSize="48px" mb={3}>🛡️</Box>
                <Text fontSize="15px" fontWeight="600" color="gray.600">No incidents recorded</Text>
                <Text fontSize="12px" color="gray.400" mt={1}>Log and track incidents for this task</Text>
              </Box>
            )}

            <VStack spacing={0} align="stretch" divider={<Box h="1px" bg="gray.100" />}>
              {incidents.map(inc => (
                <Flex key={inc.id} px={5} py={4} align="center" gap={4}
                  cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => fetchSelected(inc.uuid || inc.id).then(() => {})}>
                  <Box>
                    <Box w="10px" h="10px" borderRadius="full" bg={inc.is_open ? 'red.400' : 'green.400'} />
                  </Box>
                  <Box flex={1} minW={0}>
                    <HStack spacing={2} mb={0.5}>
                      <Text fontWeight="700" fontSize="14px" color="gray.800" noOfLines={1}>{inc.title}</Text>
                      <Badge colorScheme={STATUS_COLORS[inc.status] || 'gray'} fontSize="8px">{STATUS_LABELS[inc.status] || inc.status}</Badge>
                      {inc.sub_task_title && <Badge colorScheme="purple" fontSize="7px">🔹 {inc.sub_task_title}</Badge>}
                    </HStack>
                    <HStack spacing={3} fontSize="11px" color="gray.400">
                      <Text>{fmtDt(inc.started_at)}</Text>
                      <Text>{inc.row_count || 0} entries</Text>
                      {inc.data_leaked && <Badge colorScheme="red" fontSize="7px">Data leak</Badge>}
                      {inc.data_lost && <Badge colorScheme="red" fontSize="7px">Data loss</Badge>}
                      {inc.system_unavailable && <Badge colorScheme="orange" fontSize="7px">System down</Badge>}
                      {inc.pr_issue && <Badge colorScheme="pink" fontSize="7px">PR issue</Badge>}
                    </HStack>
                  </Box>
                  <HStack spacing={1} onClick={e => e.stopPropagation()}>
                    <Tooltip label="Delete incident">
                      <IconButton icon={<FiTrash2 size={12} />} size="xs" variant="ghost" colorScheme="red"
                        onClick={() => setDeleteConfirmId(inc.id)} />
                    </Tooltip>
                  </HStack>
                </Flex>
              ))}
            </VStack>
          </Box>
        ) : creating ? (
          <Box bg="white" borderRadius="xl" shadow="2xl" overflow="hidden">
            <Flex px={5} py={4} bg="red.50" borderBottom="1px" borderColor="red.100" align="center" justify="space-between">
              <HStack><FiAlertTriangle color="red" /><Text fontWeight="700" fontSize="15px" color="red.700">New Incident</Text></HStack>
              <Button size="sm" variant="ghost" onClick={() => setCreating(false)} leftIcon={<FiArrowLeft size={13} />}>Back</Button>
            </Flex>
            <Box p={5}>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="700">Incident Title</FormLabel>
                  <Input value={incForm.title} onChange={e => iset('title', e.target.value)} placeholder="Brief title of the incident" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Description</FormLabel>
                  <Textarea rows={4} value={incForm.description} onChange={e => iset('description', e.target.value)}
                    placeholder="Full description of the incident…" style={{ resize:'vertical' }} />
                </FormControl>
                <HStack spacing={4} flexWrap="wrap">
                  <FormControl flex={1} minW="200px">
                    <FormLabel fontSize="sm" fontWeight="700">Incident Started</FormLabel>
                    <Input type="datetime-local" value={incForm.started_at} onChange={e => iset('started_at', e.target.value)} />
                  </FormControl>
                  <FormControl flex={1} minW="200px">
                    <FormLabel fontSize="sm" fontWeight="700">Incident Finished</FormLabel>
                    <Input type="datetime-local" value={incForm.finished_at} onChange={e => iset('finished_at', e.target.value)}
                      isDisabled={incForm.is_open} />
                  </FormControl>
                </HStack>
                <Checkbox isChecked={incForm.is_open} onChange={e => iset('is_open', e.target.checked)} colorScheme="red">
                  <Text fontSize="13px" fontWeight="600" color="red.600">Open incident (ongoing / unresolved)</Text>
                </Checkbox>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">First Noticed By</FormLabel>
                  <Select value={incForm.noticed_by} onChange={e => iset('noticed_by', e.target.value)}>
                    <option value="">Select person…</option>
                    {allUsers.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
                  </Select>
                </FormControl>

                {/* Incident Classification */}
                <Box bg="red.50" p={4} borderRadius="md" border="1px" borderColor="red.200">
                  <Text fontSize="12px" fontWeight="800" textTransform="uppercase" color="red.700" mb={3} letterSpacing="wide">
                    Incident Classification
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <Checkbox isChecked={incForm.data_leaked} onChange={e => iset('data_leaked', e.target.checked)} colorScheme="red">
                      <Text fontSize="13px">Data has been leaked / exposed</Text>
                    </Checkbox>
                    <Checkbox isChecked={incForm.data_lost} onChange={e => iset('data_lost', e.target.checked)} colorScheme="red">
                      <Text fontSize="13px">Data has been lost</Text>
                    </Checkbox>
                    <Checkbox isChecked={incForm.system_unavailable} onChange={e => iset('system_unavailable', e.target.checked)} colorScheme="orange">
                      <Text fontSize="13px">System is / was unavailable</Text>
                    </Checkbox>
                    {incForm.system_unavailable && (
                      <Box ml={6}>
                        <FormLabel fontSize="xs" mb={1}>Backup Status</FormLabel>
                        <Select size="sm" value={incForm.backup_status} onChange={e => iset('backup_status', e.target.value)}>
                          <option value="current">Current and active backup</option>
                          <option value="recent">Recent backup (not fully up to date)</option>
                          <option value="long_term">Long-term backup (significantly out of date)</option>
                          <option value="not_applicable">Does not apply</option>
                        </Select>
                      </Box>
                    )}
                    <Checkbox isChecked={incForm.pr_issue} onChange={e => iset('pr_issue', e.target.checked)} colorScheme="pink">
                      <Text fontSize="13px">PR / Media issue</Text>
                    </Checkbox>
                    <Checkbox isChecked={incForm.non_disruptive} onChange={e => iset('non_disruptive', e.target.checked)} colorScheme="green">
                      <Text fontSize="13px">Non-disruptive incident</Text>
                    </Checkbox>
                  </VStack>
                </Box>

                <HStack justify="flex-end" pt={2}>
                  <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
                  <Button colorScheme="red" leftIcon={<FiAlertCircle size={15} />} onClick={handleCreateIncident}
                    isDisabled={!incForm.title}>
                    Create Incident
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </Box>
        ) : selected ? (
          <Box bg="white" borderRadius="xl" shadow="2xl" overflow="hidden">
            {/* Incident header */}
            <Box px={5} py={4} bg={selected.is_open ? 'red.50' : 'green.50'} borderBottom="1px"
              borderColor={selected.is_open ? 'red.200' : 'green.200'}>
              <Flex align="flex-start" justify="space-between" gap={3}>
                <Box flex={1}>
                  <HStack spacing={2} mb={1} flexWrap="wrap">
                    <Badge colorScheme={STATUS_COLORS[selected.status] || 'gray'} fontSize="10px" px={2} py={0.5}>
                      {STATUS_LABELS[selected.status] || selected.status}
                    </Badge>
                    <Badge colorScheme={selected.is_open ? 'red' : 'green'} fontSize="10px">
                      {selected.is_open ? '🔴 Open' : '🟢 Closed'}
                    </Badge>
                    {selected.sub_task_title && <Badge colorScheme="purple" fontSize="9px">🔹 {selected.sub_task_title}</Badge>}
                  </HStack>
                  <Text fontSize="20px" fontWeight="800" color="gray.800">{selected.title}</Text>
                  {selected.description && <Text fontSize="13px" color="gray.600" mt={1}>{selected.description}</Text>}
                  <HStack spacing={4} mt={2} fontSize="11px" color="gray.500" flexWrap="wrap">
                    <Text>Started: {fmtDt(selected.started_at)}</Text>
                    {selected.finished_at && <Text>Finished: {fmtDt(selected.finished_at)}</Text>}
                    {selected.noticed_by_name && <Text>First noticed by: {selected.noticed_by_name}</Text>}
                  </HStack>
                  {/* Classification badges */}
                  <HStack spacing={1} mt={2} flexWrap="wrap">
                    {selected.data_leaked && <Badge colorScheme="red" fontSize="8px">Data Leaked</Badge>}
                    {selected.data_lost && <Badge colorScheme="red" fontSize="8px">Data Lost</Badge>}
                    {selected.system_unavailable && <Badge colorScheme="orange" fontSize="8px">System Unavailable</Badge>}
                    {selected.system_unavailable && selected.backup_status !== 'not_applicable' && (
                      <Badge colorScheme="yellow" fontSize="8px">
                        Backup: {selected.backup_status === 'current' ? 'Current' : selected.backup_status === 'recent' ? 'Recent' : 'Long-term'}
                      </Badge>
                    )}
                    {selected.pr_issue && <Badge colorScheme="pink" fontSize="8px">PR/Media</Badge>}
                    {selected.non_disruptive && <Badge colorScheme="green" fontSize="8px">Non-disruptive</Badge>}
                  </HStack>
                </Box>
                <VStack spacing={2} flexShrink={0}>
                  <Button size="sm" variant="ghost" leftIcon={<FiArrowLeft size={13} />}
                    onClick={() => { setSelected(null); setAddingRow(false); setEditingRow(null); }}>
                    All Incidents
                  </Button>
                  <Button size="sm" colorScheme={selected.is_open ? 'green' : 'red'} variant="outline"
                    onClick={() => handleToggleOpen(selected)}>
                    {selected.is_open ? 'Close Incident' : 'Re-open'}
                  </Button>
                </VStack>
              </Flex>
            </Box>

            {/* Rows */}
            <Box p={5}>
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontWeight="700" fontSize="14px" color="gray.700">
                  Incident Log ({selected.rows?.length || 0} entries)
                </Text>
                {!addingRow && !editingRow && (
                  <Button size="sm" leftIcon={<FiPlus size={13} />} bg="#1A365D" color="white" _hover={{ bg:'#2B6CB0' }}
                    onClick={() => setAddingRow(true)}>
                    Add Entry
                  </Button>
                )}
              </Flex>

              <VStack spacing={2} align="stretch">
                {(addingRow || editingRow) && (
                  <RowForm
                    incidentId={selected.uuid || selected.id}
                    allUsers={allUsers}
                    allRows={selected.rows || []}
                    editRow={editingRow}
                    onSaved={handleRowSaved}
                    onCancel={() => { setAddingRow(false); setEditingRow(null); }}
                    currentUser={user}
                  />
                )}
                {(!selected.rows || selected.rows.length === 0) && !addingRow && (
                  <Box textAlign="center" py={8} color="gray.400">
                    <Text fontSize="13px">No entries yet. Add an entry to begin logging this incident.</Text>
                  </Box>
                )}
                {(selected.rows || []).map(row => (
                  <IncidentRow key={row.id} row={row} incident={selected}
                    allUsers={allUsers} allRows={selected.rows || []}
                    onEdit={r => { setEditingRow(r); setAddingRow(false); }}
                    onDelete={handleDeleteRow} />
                ))}
              </VStack>
            </Box>
          </Box>
        ) : null}

        {/* Delete confirm modal */}
        {deleteConfirmId && (
          <Modal isOpen={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} size="sm">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader fontSize="md" color="red.600">Delete Incident</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text fontSize="13px">Are you sure? This will permanently delete the incident and all its log entries.</Text>
              </ModalBody>
              <ModalFooter>
                <Button size="sm" variant="ghost" mr={2} onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                <Button size="sm" colorScheme="red" onClick={() => handleDeleteIncident(deleteConfirmId)}>Delete</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </Box>
    </Box>
  );
}
